import { Card, Divider, Link, Row, Text, Spinner } from '@nextui-org/react';
import React, { useState, useEffect } from 'react';
import { fetchWithJson } from "../../../lib/api";
import { error } from "../../../lib/userAlerts";
import endpoint from "../../../config/endpoint.json";

const DocumentStats = ({ clientId }) => {
    const [stats, setStats] = useState({
        receipts: 0,
        safetyDocs: 0,
        loading: true
    });

    useEffect(() => {
        if (clientId) {
            fetchAllData(clientId);
        }
    }, [clientId]);

    const fetchAllData = async (clientId) => {
        try {
            setStats(prev => ({ ...prev, loading: true }));
            await Promise.all([
                fetchReceiptData(clientId),
                fetchSafetyData(clientId)
            ]);
        } catch (err) {
            console.error(err);
            error('Unable to fetch document stats');
        } finally {
            setStats(prev => ({ ...prev, loading: false }));
        }
    };

    const fetchReceiptData = async (clientId) => {
        try {
            const res = await fetchWithJson(endpoint.statsReceipts, {
                company_id: clientId,
            });

            if (res.status !== 200) {
                error('Unable to fetch receipts stats');
                return;
            }

            const data = await res.json();
            setStats(prev => ({
                ...prev,
                receipts: data?.receipts?.unprocessedReceipts || 0
            }));
        } catch (err) {
            console.error(err);
            error('Unable to fetch receipts stats');
        }
    };

    const fetchSafetyData = async (clientId) => {
        try {
            const res = await fetchWithJson(endpoint.statsSafety, {
                company_id: clientId,
            });

            if (res.status !== 200) {
                error('Unable to fetch safety doc stats');
                return;
            }

            const data = await res.json();
            setStats(prev => ({
                ...prev,
                safetyDocs: data?.safety?.numSafetyDocs || 0
            }));
        } catch (err) {
            console.error(err);
            error('Unable to fetch safety doc stats');
        }
    };

    const StatRow = ({ label, value, link }) => (
        <Row justify="space-between" align="center" className="py-2">
            <Text size={16} color="$gray600">{label}</Text>
            <Link 
                href={link}
                css={{ 
                    color: '$primary',
                    '&:hover': { 
                        opacity: 0.8,
                        textDecoration: 'none'
                    }
                }}
            >
                <Text color='$primary' size={16} weight="semibold">{value}</Text>
            </Link>
        </Row>
    );

    return (
        <Card 
            css={{ 
                br: 0,
                minWidth: '300px',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '$lg'
                }
            }}
            bordered 
            shadow={false}
        >
            <Card.Header className="px-6 py-4">
                <Text size={20} weight="semibold" color="$gray900">
                    Documents
                </Text>
            </Card.Header>
            
            <Divider />
            
            <Card.Body css={{ br: 0, px: '$6', py: '$4' }}>
                {stats.loading ? (
                    <div className="flex justify-center items-center py-4">
                        <Spinner size="sm" color="primary" />
                    </div>
                ) : (
                    <>
                        <StatRow 
                            label="Unprocessed Receipts" 
                            value={stats.receipts.toLocaleString()} 
                            link="/driver/reimbursable-receipts-management"
                        />
                        <StatRow 
                            label="Unprocessed Safety Documents" 
                            value={stats.safetyDocs.toLocaleString()} 
                            link="/driver/safety-documents"
                        />
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default DocumentStats;